import { cn } from "@/lib/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { createElement, forwardRef } from "react";

interface DynamicTypographyProps {
  tag?: "div" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  children: React.ReactNode;
  className?: string;
}

const DynamicTypography = forwardRef<HTMLElement, DynamicTypographyProps>(
  ({ tag = "div", children, ...props }, ref) =>
    createElement(tag, { ...props, ref }, children),
);
DynamicTypography.displayName = "DynamicTypography";

type TypographyProps = {
  tag?: DynamicTypographyProps["tag"];
  children: DynamicTypographyProps["children"];
  className?: string;
} & VariantProps<typeof typographyVariants>;

const typographyVariants = cva("", {
  variants: {
    variant: {
      heading: "text-xl font-semibold text-foreground",
      subHeading: "text-[13px] leading-5 text-muted-foreground",
      pageHeading: "text-2xl font-semibold text-foreground",
      link: "text-primary underline underline-offset-4 hover:text-primary/80",
      bold: "text-sm font-semibold text-foreground",
      default: "text-[13px] leading-5 font-medium text-foreground",
    },
    weight: {
      bold: "font-bold",
      semibold: "font-semibold",
      medium: "font-medium",
      regular: "font-normal",
      default: "",
    },
    color: {
      black: "text-black",
      white: "text-white",
      primary: "text-primary",
      darkPrimary: "text-dark-primary",
      secondary: "text-secondary",
      default: "",
    },
    size: {
      default: "",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      xxl: "text-2xl",
    },
  },
  defaultVariants: {
    variant: "default",
    color: "default",
    size: "default",
    weight: "default",
  },
});

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    { tag = "div", children, variant, color, size, weight, className, ...rest },
    ref,
  ) => (
    <DynamicTypography
      tag={tag}
      className={cn(
        typographyVariants({ variant, color, size, weight }),
        className,
        "text-pretty break-words",
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </DynamicTypography>
  ),
);
Typography.displayName = "Typography";
